/**
 * Stage 2C.2: 阈值二维查表
 * 按 (field, engine, resolution) 三维查询，返回 'normal' | 'warn' | 'crit'
 *
 * 设计：
 * - 大多数字段只看 field（如 packet_loss_pct）→ default 即可
 * - 编码相关字段按 engine 分（NVENC vs x264 vs libaom 阈值差异大）
 * - encode_ms_p95 进一步按 resolution 分（1440p 软编合理 35ms，720p 只能 25ms）
 *
 * 阈值方向：
 * - 正向（warn / crit）：值 ≥ warn 即 warn，≥ crit 即 crit（如丢包/RTT/延迟）
 * - 反向（warn_below / crit_below）：值 < warn_below 即 warn（如 FPS/利用率）
 */

export type ThresholdLevel = 'normal' | 'warn' | 'crit';

interface Rule {
  warn?: number;
  crit?: number;
  warn_below?: number;
  crit_below?: number;
}

// 三层结构：field → engine → resolution → Rule
// engine 缺省用 'default'，resolution 缺省用 'default'
type ThresholdMap = Record<string, Record<string, Record<string, Rule>>>;

const thresholds: ThresholdMap = {
  // ========== 网络相关（不分 engine）==========
  packet_loss_pct: {
    default: { default: { warn: 1, crit: 3 } },
  },
  rtt_ms: {
    default: { default: { warn: 80, crit: 200 } },
  },
  jitter_buffer_ms: {
    default: { default: { warn: 50, crit: 100 } },
  },
  freeze_count: {
    default: { default: { warn: 1, crit: 5 } },
  },
  total_latency_ms: {
    default: { default: { warn: 50, crit: 100 } },
  },
  fps: {
    default: { default: { warn_below: 45, crit_below: 25 } },
  },

  // ========== 编码耗时（按 engine + resolution 分）==========
  encode_ms_p95: {
    nvenc_h264:  { default: { warn: 5, crit: 10 } },
    nvenc_h265:  { default: { warn: 5, crit: 10 } },
    nvenc_av1:   { default: { warn: 8, crit: 15 } },
    x264: {
      '1440p':   { warn: 35, crit: 50 },
      '1080p':   { warn: 25, crit: 35 },
      '720p':    { warn: 15, crit: 25 },
      'default': { warn: 25, crit: 35 },
    },
    libaom_av1:  { default: { warn: 50, crit: 80 } },
    openh264:    { default: { warn: 30, crit: 45 } },
    default:     { default: { warn: 30, crit: 50 } },
  },
  encode_ms_avg: {
    nvenc_h264:  { default: { warn: 3, crit: 5 } },
    nvenc_h265:  { default: { warn: 3, crit: 5 } },
    nvenc_av1:   { default: { warn: 5, crit: 8 } },
    x264: {
      '1440p':   { warn: 25, crit: 35 },
      '1080p':   { warn: 18, crit: 25 },
      '720p':    { warn: 10, crit: 18 },
      'default': { warn: 18, crit: 25 },
    },
    libaom_av1:  { default: { warn: 35, crit: 55 } },
    openh264:    { default: { warn: 20, crit: 30 } },
    default:     { default: { warn: 20, crit: 35 } },
  },

  // ========== QP（按 codec 分阈值范围）==========
  // H264/H265/x264 范围 0-51：>=46 红、>=41 黄
  // NVENC AV1 范围 0-255：>=200 红、>=160 黄（按比例 0-51 / 0-255 ≈ 5x）
  // libaom AV1 暂不读（host 端没采集），永远 normal
  qp_avg: {
    nvenc_h264: { default: { warn: 41, crit: 46 } },
    nvenc_h265: { default: { warn: 41, crit: 46 } },
    x264:       { default: { warn: 41, crit: 46 } },
    openh264:   { default: { warn: 41, crit: 46 } },
    nvenc_av1:  { default: { warn: 160, crit: 200 } },  // Stage 3F.1: AV1 0-255 范围
    libaom_av1: { default: {} },                          // libaom 不暴露 QP
    default:    { default: { warn: 41, crit: 46 } },
  },

  // ========== 码率利用率（反向：低于 warn_below 即告警）==========
  bitrate_utilization_pct: {
    default: { default: { warn_below: 80, crit_below: 60 } },
  },

  // ========== VBV / RC ==========
  vbv_capped: {
    default: { default: { warn: 1 } },  // bool 用 1 表示 true 触发 warn
  },

  // ========== 事件计数（30s 窗口）==========
  bwe_drop_30plus_30s: {
    default: { default: { warn: 2, crit: 5 } },
  },
  vbv_switch_30s: {
    default: { default: { warn: 5, crit: 15 } },
  },
  sudden_drop_30s: {
    default: { default: { warn: 2, crit: 5 } },
  },
  cpu_overload_30s: {
    default: { default: { warn: 1, crit: 3 } },
  },
  large_frame_30s: {
    default: { default: { warn: 3, crit: 10 } },
  },
  encoder_recovery_30s: {
    default: { default: { warn: 1, crit: 3 } },
  },
  keyframes_suppressed_30s: {
    default: { default: { warn: 5, crit: 15 } },
  },
  frames_skipped_bwe_30s: {
    // 60→50fps 跳帧约 17%（采集 60fps 但 bwe 限制 min_interval ≥ 16ms），
    // 30s 内 ~25 帧被跳是常态，warn 30 容忍稳定工况，crit 80 仍能捕捉真实问题
    default: { default: { warn: 30, crit: 80 } },
  },

  // ========== capture → encode lag（用户主观无感但数据可能异常）==========
  capture_to_encode_lag_ms: {
    default: { default: { warn: 5, crit: 15 } },
  },

  // ========== I/P 帧关系 ==========
  bitrate_keyframe_share_pct: {
    default: { default: { warn: 25, crit: 40 } },
  },

  // ========== Subscriber 相关 ==========
  subscriber_outlier_low_bitrate: {
    // 当 min_bitrate * 3 < max_bitrate 时（aggregator_reason 含 outlier_low）
    default: { default: { warn: 1 } },
  },
};

/**
 * 取阈值规则
 * 优先级：(field, engine, resolution) > (field, engine, default) > (field, default, default)
 */
function resolveRule(
  field: string,
  engine: string = 'default',
  resolution: string = 'default'
): Rule | null {
  const fieldMap = thresholds[field];
  if (!fieldMap) return null;

  const engineMap = fieldMap[engine] || fieldMap.default;
  if (!engineMap) return null;

  return engineMap[resolution] || engineMap.default || null;
}

/**
 * 评估字段当前值的告警等级
 *
 * @param field 字段名（见 thresholds 表）
 * @param value 当前值（数字；bool 字段传 1=true / 0=false）
 * @param engine 当前编码器（dominant_engine 字段值）
 * @param resolution 分辨率字符串（"1440p" / "1080p" / "720p" / "default"）
 */
export function getLevel(
  field: string,
  value: number,
  engine: string = 'default',
  resolution: string = 'default'
): ThresholdLevel {
  const rule = resolveRule(field, engine, resolution);
  if (!rule) return 'normal';

  // 反向（值越低越糟）：低于 crit_below = crit，低于 warn_below = warn
  if (rule.crit_below !== undefined && value < rule.crit_below) return 'crit';
  if (rule.warn_below !== undefined && value < rule.warn_below) return 'warn';

  // 正向（值越高越糟）：≥ crit = crit，≥ warn = warn
  if (rule.crit !== undefined && value >= rule.crit) return 'crit';
  if (rule.warn !== undefined && value >= rule.warn) return 'warn';

  return 'normal';
}

/**
 * 染色辅助：根据等级返回 Tailwind text 颜色 class
 */
export function levelToColorClass(level: ThresholdLevel): string {
  switch (level) {
    case 'crit': return 'text-red-500';
    case 'warn': return 'text-amber-500';
    default: return 'text-zinc-200';
  }
}

/**
 * 染色辅助：背景色 class（卡片头部）
 */
export function levelToBgClass(level: ThresholdLevel): string {
  switch (level) {
    case 'crit': return 'bg-red-500/10 border-red-500/30';
    case 'warn': return 'bg-amber-500/10 border-amber-500/30';
    default: return 'bg-white/5 border-white/10';
  }
}

/**
 * 简化的分辨率分类：把 width × height 映射到阈值表的 resolution key
 */
export function resolutionToKey(width: number, height: number): string {
  if (height >= 1440) return '1440p';
  if (height >= 1080) return '1080p';
  if (height >= 720)  return '720p';
  return 'default';
}
